import axios from 'axios'
import { toast } from 'sonner'
import { unwrapApiEnvelope } from '@repo/types'
import { getApiErrorMessage } from './api-error'

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Chybu zobrazí volajúci sám (inline, vlastný text) — globálny toast nižšie ju preskočí. */
    skipErrorToast?: boolean
  }
}

const API_BASE = import.meta.env.VITE_API_URL ?? ''

export const api = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  withCredentials: true,
})

// Attach access token + optional tenant override to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  const tenantId = localStorage.getItem('superadmin_tenant_id')
  if (tenantId) {
    config.headers['X-Tenant-ID'] = tenantId
  }
  return config
})

// Shared refresh: všetky volania (interceptor + useSessionKeepAlive) zdieľajú
// jednu in-flight Promise, aby sa nestalo, že paralelné refreshe
// pošlú ten istý (ešte nerotovaný) cookie a druhý dostane 401.
let refreshPromise: Promise<string> | null = null

export async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      // Refresh token je v httpOnly cookie, pošle sa automaticky (withCredentials: true)
      const { data } = await axios.post(
        `${API_BASE}/api/v1/auth/refresh`,
        {},
        { withCredentials: true },
      )
      const newToken = data?.data?.accessToken ?? data?.accessToken
      if (!newToken) throw new Error('No token in refresh response')
      localStorage.setItem('accessToken', newToken)
      return newToken
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

api.interceptors.response.use(
  (res) => {
    // Collapse single-resource envelope { data: T } → T; paginated { data, meta }
    // stays intact (array via .data, pagination via .meta). See @repo/types.
    res.data = unwrapApiEnvelope(res.data)
    return res
  },
  async (error) => {
    const originalRequest = error.config

    // Ak 401 a ešte sme neskúsili refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Neskúšaj refresh pre login/refresh endpointy
      if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')) {
        // Login (heslo aj 2FA kód): 401 = nesprávne údaje — login stránka zobrazí chybu inline;
        // reload by ju zmazal spolu so zadaným e-mailom a 2FA challenge. Na /login len pri refreshi.
        if (originalRequest.url?.includes('/auth/refresh')) {
          localStorage.removeItem('accessToken')
          window.location.href = '/login'
        }
        return Promise.reject(error)
      }

      originalRequest._retry = true

      try {
        const newToken = await refreshAccessToken()
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return api(originalRequest)
      } catch (refreshError) {
        localStorage.removeItem('accessToken')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    // Globálny toast — jediný, kto chybu API ukáže, ak request nemá `skipErrorToast` (lib/api-error.ts).
    // Bez odpovede (výpadok spojenia) len pri zápisoch: GET-y na pozadí (refetch, polling) by
    // toastovali bez akcie používateľa. 401 rieši refresh vyššie, zrušený request mlčí.
    const status = error.response?.status
    const show = status
      ? status >= 400 && status !== 401
      : !axios.isCancel(error) && originalRequest?.method !== 'get'
    if (show && !originalRequest?.skipErrorToast) {
      const msg = getApiErrorMessage(error)
      // id = text: retry dotazu ani súbežné requesty tú istú hlášku nezdvoja.
      toast.error(msg, { id: msg })
    }

    return Promise.reject(error)
  },
)
